import {
  Product,
  ProductCategory,
  ProductFilters,
  PaginatedResponse,
  ApiResponse,
  PaginationParams,
  SearchSuggestion
} from '../../types/api'
import { apiClient } from '../../utils/api'

export class ProductService {
  private readonly basePath = '/products'

  // CRUD Operations
  async getProducts(
    filters?: ProductFilters,
    pagination?: PaginationParams
  ): Promise<ApiResponse<PaginatedResponse<Product>>> {
    const params = {
      ...filters,
      ...pagination
    }
    return apiClient.get(`${this.basePath}`, params)
  }

  async getProduct(id: string): Promise<ApiResponse<Product>> {
    return apiClient.get(`${this.basePath}/${id}`)
  }

  async createProduct(data: Omit<Product, 'id' | 'created_at' | 'updated_at'>): Promise<ApiResponse<Product>> {
    return apiClient.post(`${this.basePath}`, data)
  }

  async updateProduct(id: string, data: Partial<Product>): Promise<ApiResponse<Product>> {
    return apiClient.put(`${this.basePath}/${id}`, data)
  }

  async deleteProduct(id: string): Promise<ApiResponse<void>> {
    return apiClient.delete(`${this.basePath}/${id}`)
  }

  // Search and Barcode
  async searchProducts(query: string): Promise<ApiResponse<Product[]>> {
    return apiClient.get(`${this.basePath}/search`, { q: query })
  }

  async getProductSuggestions(query: string): Promise<ApiResponse<SearchSuggestion[]>> {
    return apiClient.get(`${this.basePath}/suggestions`, { q: query })
  }

  async getProductByBarcode(barcode: string): Promise<ApiResponse<Product | null>> {
    return apiClient.get(`${this.basePath}/barcode/${barcode}`)
  }

  async getProductByCode(code: string): Promise<ApiResponse<Product | null>> {
    return apiClient.get(`${this.basePath}/code/${code}`)
  }

  async generateBarcode(id: string): Promise<ApiResponse<{ barcode: string }>> {
    return apiClient.post(`${this.basePath}/${id}/generate-barcode`)
  }

  // Categories
  async getCategories(): Promise<ApiResponse<ProductCategory[]>> {
    return apiClient.get('/product-categories')
  }

  async createCategory(data: Omit<ProductCategory, 'id' | 'created_at' | 'updated_at'>): Promise<ApiResponse<ProductCategory>> {
    return apiClient.post('/product-categories', data)
  }

  async updateCategory(id: string, data: Partial<ProductCategory>): Promise<ApiResponse<ProductCategory>> {
    return apiClient.put(`/product-categories/${id}`, data)
  }

  async deleteCategory(id: string): Promise<ApiResponse<void>> {
    return apiClient.delete(`/product-categories/${id}`)
  }

  async getProductsByCategory(categoryId: string): Promise<ApiResponse<Product[]>> {
    return apiClient.get(`${this.basePath}/category/${categoryId}`)
  }

  // Inventory Management
  async updateStock(id: string, quantity: number, type: 'add' | 'subtract' | 'set'): Promise<ApiResponse<Product>> {
    return apiClient.patch(`${this.basePath}/${id}/stock`, { quantity, type })
  }

  async getStockHistory(id: string): Promise<ApiResponse<Array<{
    id: string
    type: 'add' | 'subtract' | 'set' | 'sale' | 'adjustment'
    quantity: number
    previous_quantity: number
    new_quantity: number
    reason?: string
    created_at: string
  }>>> {
    return apiClient.get(`${this.basePath}/${id}/stock-history`)
  }

  async getLowStockProducts(threshold?: number): Promise<ApiResponse<Product[]>> {
    return apiClient.get(`${this.basePath}/low-stock`, { threshold })
  }

  async getOutOfStockProducts(): Promise<ApiResponse<Product[]>> {
    return apiClient.get(`${this.basePath}/out-of-stock`)
  }

  // Pricing
  async updatePrice(id: string, price: number): Promise<ApiResponse<Product>> {
    return apiClient.patch(`${this.basePath}/${id}/price`, { price })
  }

  async getPriceHistory(id: string): Promise<ApiResponse<Array<{
    id: string
    price: number
    previous_price: number
    effective_date: string
    created_at: string
  }>>> {
    return apiClient.get(`${this.basePath}/${id}/price-history`)
  }

  async bulkUpdatePrices(updates: Array<{ id: string, price: number }>): Promise<ApiResponse<{
    updated: number
    failed: Array<{ id: string, error: string }>
  }>> {
    return apiClient.put(`${this.basePath}/bulk-price-update`, { updates })
  }

  // Analytics
  async getProductStatistics(id: string, period?: string): Promise<ApiResponse<{
    total_sales: number
    total_quantity_sold: number
    revenue: number
    average_selling_price: number
    profit_margin: number
    sales_trend: Array<{ date: string, quantity: number, amount: number }>
  }>> {
    return apiClient.get(`${this.basePath}/${id}/statistics`, { period })
  }

  async getTopSellingProducts(limit: number = 10, period?: string): Promise<ApiResponse<Array<{
    product: Product
    quantity_sold: number
    revenue: number
    sales_count: number
  }>>> {
    return apiClient.get(`${this.basePath}/top-selling`, { limit, period })
  }

  async getProductPerformance(filters?: {
    category_id?: string
    date_from?: string
    date_to?: string
  }): Promise<ApiResponse<Array<{
    product: Product
    sales_count: number
    quantity_sold: number
    revenue: number
    profit: number
    profit_margin: number
  }>>> {
    return apiClient.get(`${this.basePath}/performance`, filters)
  }

  // Bulk Operations
  async bulkUpdate(ids: string[], data: Partial<Product>): Promise<ApiResponse<{
    updated: number
    failed: Array<{ id: string, error: string }>
  }>> {
    return apiClient.put(`${this.basePath}/bulk`, { ids, data })
  }

  async bulkDelete(ids: string[]): Promise<ApiResponse<{
    deleted: number
    failed: Array<{ id: string, error: string }>
  }>> {
    return apiClient.delete(`${this.basePath}/bulk`, { ids })
  }

  async exportProducts(filters?: ProductFilters, format: 'excel' | 'csv' = 'excel'): Promise<ApiResponse<{ url: string }>> {
    return apiClient.post(`${this.basePath}/export`, { filters, format })
  }

  // Import
  async importProducts(file: File): Promise<ApiResponse<{
    imported: number
    updated: number
    failed: number
    errors?: Array<{ row: number, errors: string[] }>
  }>> {
    const formData = new FormData()
    formData.append('file', file)
    return apiClient.post(`${this.basePath}/import`, formData)
  }

  // Image Management
  async uploadImage(id: string, image: File): Promise<ApiResponse<Product>> {
    const formData = new FormData()
    formData.append('image', image)
    return apiClient.post(`${this.basePath}/${id}/image`, formData)
  }

  async deleteImage(id: string): Promise<ApiResponse<Product>> {
    return apiClient.delete(`${this.basePath}/${id}/image`)
  }

  // Availability and Status
  async toggleAvailability(id: string): Promise<ApiResponse<Product>> {
    return apiClient.patch(`${this.basePath}/${id}/toggle-availability`)
  }

  async setInStock(id: string, inStock: boolean): Promise<ApiResponse<Product>> {
    return apiClient.patch(`${this.basePath}/${id}/stock-status`, { in_stock: inStock })
  }

  // Recent Products
  async getRecentProducts(limit: number = 10): Promise<ApiResponse<Product[]>> {
    return apiClient.get(`${this.basePath}/recent`, { limit })
  }

  async getRecentlyUpdated(limit: number = 10): Promise<ApiResponse<Product[]>> {
    return apiClient.get(`${this.basePath}/recently-updated`, { limit })
  }

  // Recommendations
  async getRelatedProducts(id: string, limit: number = 5): Promise<ApiResponse<Product[]>> {
    return apiClient.get(`${this.basePath}/${id}/related`, { limit })
  }

  async getFrequentlyBoughtTogether(id: string, limit: number = 5): Promise<ApiResponse<Product[]>> {
    return apiClient.get(`${this.basePath}/${id}/frequently-bought-together`, { limit })
  }
}

// Create and export service instance
export const productService = new ProductService() 